import { useEffect, useState } from 'react';
import { Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import { useToast } from '../../../../../context/ToastContext';
import {
  useDeleteGalleryMutation,
  useGetGalleryQuery
} from '../../../../../services/endpoints/website/gallery';
import { Gallery } from '../../../../../types/website.types';
import { dateToUTC } from '../../../../../utils/dateUtils';
import { getErrorMessage } from '../../../../../utils/utils';

export const GalleryDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data, error, isLoading } = useGetGalleryQuery(+params.id!);
  const [gallery, setGallery] = useState<Gallery>();
  const [deleteGallery, { isLoading: isDeleting }] = useDeleteGalleryMutation();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading gallery', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data) {
      setGallery(data.gallery);
    }
  }, [data]);

  const handleDelete = async () => {
    try {
      await deleteGallery(+params.id!).unwrap();
      addToast('Gallery deleted successfully', 'success');
      navigate('/admin/website/galleries');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  if (isLoading || !gallery) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate('/admin/website/galleries')}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            Galleries List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`Gallery #${gallery?.id}`}</h2>
        </Stack>
        <Stack
          direction="horizontal"
          gap={1}
          className="d-none d-lg-flex"
        >
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => navigate(`/admin/website/galleries/${gallery?.id}/edit`)}
          >
            Edit Gallery
          </SimpleButton>
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => setShowModal(true)}
          >
            Delete Gallery
          </SimpleButton>
        </Stack>
      </div>
      <div>
        <Stack
          gap={4}
          className="p-4"
        >
          <DetailSection>
            <LabelText
              label="Name:"
              text={gallery?.name}
            />
            <LabelText
              label="Created At:"
              text={
                gallery?.created_at
                  ? dateToUTC(new Date(gallery?.created_at), 'MM/dd/yyyy H:mm')
                  : 'N/A'
              }
            />
            <LabelText
              label="Updated At:"
              text={
                gallery?.updated_at
                  ? dateToUTC(new Date(gallery?.updated_at), 'MM/dd/yyyy H:mm')
                  : 'N/A'
              }
            />
          </DetailSection>
        </Stack>
      </div>
      <ConfirmationModal
        show={showModal}
        title="Delete Gallery"
        message="Are you sure you want to delete this gallery?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isDeleting}
      />
    </>
  );
};
